import React, { FC, useEffect } from 'react';
import ReactHtmlParser from 'react-html-parser';
import styled from 'styled-components';
import apiRoutes from '../../../config/api/routes';
import { getContentPageMapper } from '../../../config/api/models/ContentPage';
import { useFetch, usePublicPageSettings } from '../../shared/hooks';
import { Message, Icons, Loading, PageTitle, PageTitleIconPrefix } from '../../shared/components';

type Props = {
  id: string;
  icon?: Icons;
  autoHeight?: boolean;
  plainPage?: boolean;
  omitTitle?: boolean;
  titleForceInline?: boolean;
};

const ContentWrapper = styled(({ autoHeight, ...restProps }) => <div {...restProps} />)`
  height: ${({ autoHeight }) => (autoHeight ? '100%' : 'calc(100% - 5.5rem)')};
  & img {
    max-width: 100%;
  }
  overflow-y: auto;
  ${({ theme }) => theme.breakpoints.down('xs')} {
    height: ${({ autoHeight }) => (autoHeight ? '100%' : 'calc(100% - 4.5rem)')};
  }
`;

const PageWrapper = styled(({ autoHeight, plainPage, ...restProps }) => <div {...restProps} />)`
  height: ${({ autoHeight }) => (autoHeight ? 'auto' : 'calc(100vh - 30rem)')};
  padding: ${({ plainPage }) => (plainPage ? '20px' : '0')};
`;

const StyledPageTitleIconPrefix = styled(PageTitleIconPrefix)`
  background-color: yellow;
  ${(props) => props.theme.breakpoints.down('xs')} {
    background-color: red;
    display: none;
  }
`;

const ContentPage: FC<Props> = ({
  id,
  icon,
  plainPage = false,
  autoHeight = false,
  omitTitle = false,
  titleForceInline = false,
}) => {
  const [, setPublicPageSettings] = usePublicPageSettings();
  useEffect(() => {
    setPublicPageSettings({ pageWidth: '84rem' });
  }, [setPublicPageSettings]);

  const [getContentPageResponse, getContentPage] = useFetch(apiRoutes.contentPages(id), {
    mapper: getContentPageMapper,
  });

  useEffect(() => {
    if (!id) return;
    getContentPage();
  }, [getContentPage, id]);

  return (
    <PageWrapper autoHeight={autoHeight} plainPage={plainPage}>
      {getContentPageResponse.pending && <Loading />}
      {getContentPageResponse.error && (
        <Message type="error">{getContentPageResponse.reason}</Message>
      )}
      {getContentPageResponse.success && (
        <>
          {!omitTitle && (
            <PageTitle
              forceInline={titleForceInline}
              prefix={icon ? <StyledPageTitleIconPrefix icon={icon} /> : undefined}
            >
              {getContentPageResponse.data.title}
            </PageTitle>
          )}
          <ContentWrapper autoHeight={autoHeight}>
            {ReactHtmlParser(getContentPageResponse.data.body)}
          </ContentWrapper>
        </>
      )}
    </PageWrapper>
  );
};

export default ContentPage;
